/* Independent source union. Product tags and attributes are read, never changed. */
((root,factory)=>{const api=factory();if(typeof module==='object')module.exports=api;else root.CatalogSources=api;})(typeof globalThis==='object'?globalThis:this,()=>{
  'use strict';
  const normal=value=>String(value || '').normalize('NFKC').trim().replace(/\s+/g,' ').toLocaleLowerCase('ro');
  const text=value=>normal(value).normalize('NFD').replace(/[\u0300-\u036f]/g,'');
  function dictionary(groups=[],legacy=[]) {
    return groups.map(g=>{
      const seen=new Set(),old=legacy.find(x=>x.key===g.key);
      return {...g,options:[...(g.chunks || []).flat(),...(old?.chunks || []).flat()].filter(o=>!seen.has(o.tag) && seen.add(o.tag))};
    });
  }
  function enrich(product,groups) {
    const tags=new Set(product.tags || []),values=[],labels=[];
    for(const g of groups) {
      const normalize=g.native?value=>text(value):normal;
      const a=new Set([...(product.attributes?.[g.metafield] || []),...(product.native?.[g.metafield] || [])].map(normalize));
      for(const o of g.options)if(tags.has(o.tag) || a.has((g.native?text:normal)(o.label))){values.push(o.tag);labels.push(o.label);}
    }
    return {...product,values:[...new Set(values)],labels:[...new Set(labels)]};
  }
  function terms(query) {return /[:"()|]|\b(?:OR|AND|NOT)\b/.test(query)?[]:text(query).split(/\s+/).filter(Boolean);}
  function attributeMatch(product,query) {
    const words=terms(query);if(!words.length)return false;
    const attrs=text((product.labels || []).join(' ')),body=text([product.title,product.vendor,product.type,...(product.skus || []),attrs].join(' '));
    return words.some(w=>attrs.includes(w)) && words.every(w=>body.includes(w));
  }
  function matches(product,url) {
    for(const [key,value] of url.searchParams) {
      if(key.startsWith('attribute_') && value && !product.values.includes(value))return false;
    }
    const min=url.searchParams.get('filter.v.price.gte'),max=url.searchParams.get('filter.v.price.lte');
    if((min || max) && !(product.prices || [product.price]).some(p=>(!min || p>=Number(min)*100) && (!max || p<=Number(max)*100)))return false;
    for(const [key,prop] of [['filter.p.product_type','type'],['filter.p.vendor','vendor']]) {
      const selected=url.searchParams.getAll(key).filter(Boolean);if(selected.length && !selected.includes(product[prop]))return false;
    }
    const stock=url.searchParams.getAll('filter.v.availability');
    if(stock.length===1 && product.available!==(stock[0]==='1'))return false;
    const tags=url.searchParams.getAll('filter.p.tag').filter(Boolean);
    if(tags.length && !tags.some(t=>(product.tags || []).includes(t)))return false;
    return true;
  }
  function merge(native,supplemental) {const seen=new Set();return [...native,...supplemental].filter(p=>!seen.has(p.id) && seen.add(p.id));}
  function sort(products,order) {
    const out=[...products],collator=new Intl.Collator('ro',{numeric:true,sensitivity:'base'});
    if(order==='price-ascending')out.sort((a,b)=>a.price-b.price);
    if(order==='price-descending')out.sort((a,b)=>b.price-a.price);
    if(order==='title-ascending')out.sort((a,b)=>collator.compare(a.title,b.title));
    if(order==='title-descending')out.sort((a,b)=>collator.compare(b.title,a.title));
    if(order==='created-ascending')out.sort((a,b)=>Number(a.created)-Number(b.created));
    if(order==='created-descending')out.sort((a,b)=>Number(b.created)-Number(a.created));
    return out;
  }
  const api={normal,text,dictionary,enrich,terms,attributeMatch,matches,merge,sort};
  if(typeof document==='undefined')return api;
  const config=JSON.parse(document.querySelector('[data-catalog-sources]')?.textContent || '{}');
  const groups=dictionary(config.groups || [],config.legacy || []),byTag=new Map(groups.flatMap(g=>g.options.map(o=>[o.tag,{...o,key:g.key}])));
  const cache=new Map();
  function preview(url) {const id=new URL(location.href).searchParams.get('preview_theme_id') || (window.Shopify?.theme?.role!=='main'?window.Shopify?.theme?.id:null);if(id)url.searchParams.set('preview_theme_id',id);return url;}
  function canonical(input) {
    const url=new URL(input,location.origin),match=url.pathname.match(/^(.*\/collections\/[^/]+)(?:\/(.*))?$/);
    if(match?.[2]) {
      const keep=[];
      for(const token of match[2].split('+').map(decodeURIComponent)){const option=byTag.get(token);if(option)url.searchParams.set('attribute_'+option.key,token);else keep.push(token);}
      url.pathname=match[1]+(keep.length?'/'+keep.map(encodeURIComponent).join('+'):'');
    }
    const old=url.searchParams.getAll('filter.p.tag');url.searchParams.delete('filter.p.tag');
    for(const token of old){const option=byTag.get(token);if(option)url.searchParams.set('attribute_'+option.key,token);else url.searchParams.append('filter.p.tag',token);}
    return url;
  }
  async function load(input) {
    const url=preview(new URL(input,location.origin));url.searchParams.set('view','catalog-data');
    for(const key of [...url.searchParams.keys()])if(key.startsWith('attribute_') || key==='page' || key==='section_id')url.searchParams.delete(key);
    const key=url.href,old=cache.get(key);if(old && Date.now()-old.at<60000)return old.promise;
    const page=async number=>{
      const target=new URL(url);target.searchParams.set('page',String(number));
      const response=await fetch(target,{headers:{Accept:'text/html'}});if(!response.ok)throw new Error('Catalog HTTP '+response.status);
      const data=await response.json();if(data.version!==1 || !Array.isArray(data.products))throw new Error('Catalog invalid');return data;
    };
    const promise=(async()=>{
      const first=await page(1),all=[first];
      if(first.total>25000)throw new Error('Catalogul depășește limita de paginare Shopify.');
      for(let n=2;n<=first.pages;n+=3)all.push(...await Promise.all(Array.from({length:Math.min(3,first.pages-n+1)},(_,i)=>page(n+i))));
      const products=merge([],all.flatMap(p=>p.products)).map(p=>enrich(p,groups));
      if(products.length!==first.total)throw new Error('Catalogul s-a schimbat. Reîncearcă filtrarea.');
      return products;
    })();
    cache.set(key,{at:Date.now(),promise});promise.catch(()=>cache.delete(key));return promise;
  }
  async function results(input) {
    const url=canonical(input),search=url.pathname.replace(/\/$/,'')===config.searchUrl.replace(/\/$/,'');
    const native=await load(url);let candidates=native;
    if(search && terms(url.searchParams.get('q') || '').length) {
      const all=await load(config.allUrl);
      candidates=merge(native,all.filter(p=>attributeMatch(p,url.searchParams.get('q')) && matches(p,url)));
    }
    return sort(candidates.filter(p=>matches(p,url)),url.searchParams.get('sort_by'));
  }
  async function cards(products,signal) {
    const batches=[];
    for(let i=0;i<products.length;i+=12)batches.push(products.slice(i,i+12));
    const html=await Promise.all(batches.map(async batch=>{
      const url=preview(new URL(config.searchUrl,location.origin));url.searchParams.set('view','catalog-cards');url.searchParams.set('type','product');url.searchParams.set('q',batch.map(p=>p.handle).join('|'));
      const r=await fetch(url,{signal});if(!r.ok)throw new Error('Carduri HTTP '+r.status);return r.text();
    }));
    const template=document.createElement('template');template.innerHTML=html.join('');
    const found=[...template.content.querySelectorAll('article[data-product-id]')];
    if(found.length!==products.length || found.some((el,i)=>el.dataset.productId!==String(products[i].id)))throw new Error('Produsele s-au schimbat. Reîncearcă.');
    return template.content;
  }
  async function suggest(query,native=[]) {
    const all=await load(config.allUrl),extra=all.filter(p=>attributeMatch(p,query));
    return merge(native.map(p=>({...p,id:String(p.id)})),extra.map(p=>({...p,price:p.price/100}))).slice(0,5);
  }
  Object.assign(api,{config,groups,byTag,canonical,load,results,cards,suggest,preview});return api;
});
